import { HttpClient, HttpHeaders } from '@angular/common/http';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { authOrgSelector } from 'src/app/auth/store/auth.selectors';
import { IFeatures } from '@bciti/types';
import { OrganisationPayload, Navigation, OrganisationConfiguration, GetAdminOrganisationAreas, Colour } from '../../global.model';
import { OrganisationService } from './organisation.abstract.service';
import { ADMIN_BROSSARD_ORGANISATION_AREAS_MOCK } from '../../global.mock';
import { organisationTagSelector } from '../../store/global.selectors';

@Injectable()
export class OrganisationImplService implements OrganisationService {
    public baseUrl: string = environment.API_GATEWAY_URL;

    private subdomainPrefixes = ['dev-admin-', 'test-admin-', 'uat-admin-', 'admin-', 'dev-', 'test-', 'uat-'];

    constructor(
        private store: Store<State>,
        private http: HttpClient,
    ) {}

    public getSubdomain(): Observable<undefined | string> {
        const parts = location.hostname.split('.');
        if (parts.length < 3) {
            return of();
        }
        const subdomain = parts[0];
        const strippedSubdomain = this.stripSubdomainPrefix(subdomain);
        return of(strippedSubdomain);
    }

    private stripSubdomainPrefix(subdomain: string): string {
        for (const prefix of this.subdomainPrefixes) {
            const index = subdomain.indexOf(prefix);
            if (index === 0) {
                return subdomain.substr(prefix.length);
            }
        }

        return subdomain;
    }

    public getOrganisations(): Observable<OrganisationPayload[]> {
        return this.http.get(`${this.baseUrl}/organisations`).pipe(map((response) => response as OrganisationPayload[]));
    }

    public getOrganisationNavigation(tag: string): Observable<Navigation> {
        return this.http
            .get(`${this.baseUrl}/contents/admin-menu`, {
                headers: new HttpHeaders({
                    'x-organisation': tag,
                }),
            })
            .pipe(map((response) => response as Navigation));
    }

    public getOrganisationByTag(tag: string): Observable<OrganisationPayload> {
        return this.http.get(`${this.baseUrl}/organisations/${tag}`).pipe(map((response) => response as OrganisationPayload));
    }

    public getOrganisationBySubdomain(subdomain: string): Observable<OrganisationPayload> {
        return this.http.get(`${this.baseUrl}/organisations/subdomain/${subdomain}`).pipe(map((response) => response as OrganisationPayload));
    }

    public getOrganisationFromAuth(): Observable<OrganisationPayload> {
        return this.store.select(authOrgSelector).pipe(
            filter((tag): tag is string => !!tag),
            take(1),
            switchMap((tag) => this.getOrganisationByTag(tag)),
        );
    }

    public getOrganisationConfiguration(tag: string): Observable<OrganisationConfiguration> {
        return this.http
            .get(`${this.baseUrl}/configurations/platform`, {
                headers: new HttpHeaders({
                    'x-organisation': tag,
                }),
            })
            .pipe(map((response) => response as OrganisationConfiguration));
    }

    public getOrganisationFeaturesConfiguration(tag: string): Observable<IFeatures> {
        return this.http
            .get(`${this.baseUrl}/configurations/features`, {
                headers: new HttpHeaders({
                    'x-organisation': tag,
                }),
            })
            .pipe(map((response) => response as IFeatures));
    }

    public getOrganisationAreas(): Observable<GetAdminOrganisationAreas[]> {
        return this.http.get(`${this.baseUrl}/organisations/admin/areas`).pipe(map((response) => response as GetAdminOrganisationAreas[]));
    }

    public getOrganisationAreasByTag(): Observable<GetAdminOrganisationAreas[]> {
        return this.store.select(organisationTagSelector).pipe(
            filter((tag): tag is string => !!tag),
            take(1),
            switchMap((tag) => this.http.get(`${this.baseUrl}/organisations/admin/${tag}/areas`)),
            map((response) => response as GetAdminOrganisationAreas[]),
        );
    }

    public createOrganisationArea(area: any): Observable<GetAdminOrganisationAreas> {
        return this.store.select(organisationTagSelector).pipe(
            take(1),
            switchMap((tag) => this.http.post(`${this.baseUrl}/organisations/admin/${tag}/areas`, area)),
            map((response) => response as GetAdminOrganisationAreas),
        );
    }

    public updateOrganisationArea(area: GetAdminOrganisationAreas): Observable<GetAdminOrganisationAreas> {
        return this.store.select(organisationTagSelector).pipe(
            take(1),
            switchMap((tag) => this.http.patch(`${this.baseUrl}/organisations/admin/${tag}/areas/${area._id}`, area)),
            map((response) => response as GetAdminOrganisationAreas),
        );
    }

    public deleteOrganisationArea(id: string): Observable<any> {
        return this.store.select(organisationTagSelector).pipe(
            take(1),
            switchMap((tag) => this.http.delete(`${this.baseUrl}/organisations/admin/${tag}/areas/${id}`)),
            map((response) => response),
        );
    }

    public setOrganisationColour(orgTag: string, colour: Colour): Observable<any> {
        return this.store.select(organisationTagSelector).pipe(
            take(1),
            switchMap((tag) => this.http.put(`${this.baseUrl}/organisations/admin/${orgTag}/colour`, colour)),
            map((response) => response),
        );
    }

    public getOrganisationColour(): Observable<any> {
        return this.store.select(organisationTagSelector).pipe(
            take(1),
            switchMap((tag) => this.http.get(`${this.baseUrl}/organisations/admin/${tag}/colour`)),
            map((response: any) => response),
        );
    }
}

export const ORGANISATION_SERVICE_PROVIDER = {
    provide: OrganisationService,
    useClass: OrganisationImplService,
};
